/**
 * Custom hook for managing dietician referral details logic
 *
 * Handles fetching referral data, appointment data, and related state management
 * for the dietician referral details page.
 *
 * @module hooks/useDieticianReferralDetails
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { apiService } from '@/services/ApiService';
import type { Referral } from '@/types/dashboard';
import type { Referral as ApiReferral } from '@/services/domains/ReferralDomain';
import type { CalendlyAppointment } from '@/types/calendlyAppointments';
import { CalendlyAppointmentApiError } from '@/types/calendlyAppointments';
import { getLogger } from '@/services/shared/LoggerFactory';

interface UseDieticianReferralDetailsProps {
  referralId?: string;
}

interface UseDieticianReferralDetailsReturn {
  referral: Referral | null;
  loading: boolean;
  error: string | null;
  appointment: CalendlyAppointment | null;
  appointmentLoading: boolean;
  appointmentError: CalendlyAppointmentApiError | null;
  photoUrls: Record<string, string | null>;
  photoUrlsLoading: boolean;
  photoUrlsError: string | null;
  handleRefreshAppointment: () => Promise<void>;
  retryFetchAppointment: () => Promise<void>;
}

/**
 * Transform API referral to dashboard referral format
 */
const transformApiReferral = (apiReferral: ApiReferral): Referral => {
  return {
    id: apiReferral.id,
    referralToken: apiReferral.referral_token,
    referringPhysicianId: apiReferral.referring_physician_id,
    dietitianId: apiReferral.dietitian_id || '',
    patientName: apiReferral.patient_name || '',
    dateOfBirth: apiReferral.date_of_birth || '',
    patientIdNumber: apiReferral.patient_id_number || '',
    patientIdType: apiReferral.patient_id_type || '',
    phone: apiReferral.phone || '',
    email: apiReferral.email || '',
    conditions: apiReferral.conditions || [],
    additionalNotes: apiReferral.additional_notes,
    status: apiReferral.status as 'pending' | 'claimed' | 'scheduled' | 'expired' | 'cancelled',
    claimedByUserId: apiReferral.claimed_by_user_id,
    claimedAt: apiReferral.claimed_at,
    expiresAt: apiReferral.expires_at,
    createdAt: apiReferral.created_at,
    updatedAt: apiReferral.updated_at,
    referringPhysicianName: apiReferral.referring_physician_name,
    referringPhysicianEmail: apiReferral.referring_physician_email || '',
    dietitianName: apiReferral.dietitian_name || '',
    dietitianEmail: apiReferral.dietitian_email || '',
  };
};

/**
 * Custom hook for managing dietician referral details
 */
export const useDieticianReferralDetails = ({
  referralId
}: UseDieticianReferralDetailsProps): UseDieticianReferralDetailsReturn => {
  const logger = getLogger();
  const navigate = useNavigate();

  // State management
  const [referral, setReferral] = useState<Referral | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [appointment, setAppointment] = useState<CalendlyAppointment | null>(null);
  const [appointmentLoading, setAppointmentLoading] = useState(false);
  const [appointmentError, setAppointmentError] = useState<CalendlyAppointmentApiError | null>(null);
  const [photoUrls, setPhotoUrls] = useState<Record<string, string | null>>({});
  const [photoUrlsLoading, setPhotoUrlsLoading] = useState(false);
  const [photoUrlsError, setPhotoUrlsError] = useState<string | null>(null);

  // Fetch referral details from API
  useEffect(() => {
    const fetchReferralDetails = async () => {
      if (!referralId) {
        setError('No referral ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const apiReferral = await apiService.referral.getReferral(referralId);
        const transformedReferral = transformApiReferral(apiReferral);
        setReferral(transformedReferral);

        // Fetch photo URLs for relevant users
        await fetchPhotoUrls(apiReferral);
      } catch (error) {
        logger.error('Failed to fetch referral details:', error);

        // Check if it's a 403 error and redirect to dashboard
        if (error && typeof error === 'object' && 'status' in error && error.status === 403) {
          logger.warn('Access denied (403) for referral details, redirecting to dashboard');
          navigate('/dashboard', { replace: true });
          return; // Exit early to prevent setting error state
        }

        setError('Failed to load referral details');
      } finally {
        setLoading(false);
      }
    };

    fetchReferralDetails();
  }, [referralId, navigate]);

  // Fetch photo URLs for referral-related users
  const fetchPhotoUrls = async (apiReferral: ApiReferral) => {
    try {
      setPhotoUrlsLoading(true);
      setPhotoUrlsError(null);

      // Collect user IDs that we need photos for
      const userIds: string[] = [];

      // Add referring physician ID
      if (apiReferral.referring_physician_id) {
        userIds.push(apiReferral.referring_physician_id);
      }

      // Add dietitian ID if assigned
      if (apiReferral.dietitian_id) {
        userIds.push(apiReferral.dietitian_id);
      }

      // Add claimed by user ID if claimed
      if (apiReferral.claimed_by_user_id) {
        userIds.push(apiReferral.claimed_by_user_id);
      }

      // Only fetch if we have user IDs
      if (userIds.length > 0) {
        const photoResponse = await apiService.photo.getPhotoUrls({ user_ids: userIds });
        setPhotoUrls(photoResponse.photo_urls);
      }
    } catch (error) {
      logger.error('Failed to fetch photo URLs:', error);
      setPhotoUrlsError('Failed to load photo URLs');
    } finally {
      setPhotoUrlsLoading(false);
    }
  };

  // Fetch calendly appointment if referral status is not pending and claimed
  useEffect(() => {
    const fetchCalendlyAppointment = async () => {
      if (!referral || !referral.id) return;

      // Only fetch appointment if status is not pending and claimed
      if (referral.status === 'pending' || referral.status === 'claimed') {
        return;
      }

      try {
        setAppointmentLoading(true);
        setAppointmentError(null);

        // Get appointments
        const appointmentsResponse = await apiService.calendly.getMyAppointments();

        // Filter by claimedByUserId
        const appointmentData = appointmentsResponse.data.find(
          appointment => appointment.patientId === referral.claimedByUserId
        );

        // Store as const appointmentData
        setAppointment(appointmentData || null);
      } catch (error) {
        logger.error('Failed to fetch calendly appointment:', error);
        setAppointmentError(new CalendlyAppointmentApiError(
          'Failed to load appointment details',
          500,
          undefined,
          'UNKNOWN_ERROR'
        ));
      } finally {
        setAppointmentLoading(false);
      }
    };

    fetchCalendlyAppointment();
  }, [referral]);

  // Handler for refreshing appointment data
  const handleRefreshAppointment = async () => {
    if (!appointment?.appointmentId) return;

    try {
      setAppointmentLoading(true);
      setAppointmentError(null);

      // Refresh appointment data
      const refreshedAppointment = await apiService.calendly.refreshAppointment(appointment.appointmentId);
      setAppointment(refreshedAppointment);
    } catch (error) {
      logger.error('Failed to refresh appointment:', error);
      setAppointmentError(new CalendlyAppointmentApiError(
        'Failed to refresh appointment details',
        500,
        undefined,
        'UNKNOWN_ERROR'
      ));
    } finally {
      setAppointmentLoading(false);
    }
  };

  // Handler for retrying appointment fetch
  const retryFetchAppointment = async () => {
    if (!referral || !referral.id) return;

    try {
      setAppointmentLoading(true);
      setAppointmentError(null);

      const appointmentsResponse = await apiService.calendly.getMyAppointments();
      const appointmentData = appointmentsResponse.data.find(
        appointment => appointment.patientId === referral.claimedByUserId
      );
      setAppointment(appointmentData || null);
    } catch (error) {
      logger.error('Failed to fetch calendly appointment:', error);
      setAppointmentError(new CalendlyAppointmentApiError(
        'Failed to load appointment details',
        500,
        undefined,
        'UNKNOWN_ERROR'
      ));
    } finally {
      setAppointmentLoading(false);
    }
  };

  return {
    referral,
    loading,
    error,
    appointment,
    appointmentLoading,
    appointmentError,
    photoUrls,
    photoUrlsLoading,
    photoUrlsError,
    handleRefreshAppointment,
    retryFetchAppointment,
  };
};